'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  consultationType: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  industry: string;
  employeeCount: string;
  urgency: string;
  services: string[];
  message: string;
  preferredContact: string;
  preferredTime: string;
}

export default function ConsultationForm() {
  const [formData, setFormData] = useState<FormData>({
    consultationType: 'strategic',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    employeeCount: '',
    urgency: 'normal',
    services: [],
    message: '',
    preferredContact: 'email',
    preferredTime: 'morning'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const industries = [
    'Healthcare',
    'Finance & Banking',
    'Manufacturing',
    'Retail & E-commerce',
    'Education',
    'Technology',
    'Professional Services',
    'Other'
  ];

  const serviceOptions = [
    'IT Infrastructure',
    'Cybersecurity',
    'Cloud Solutions',
    'Managed Services',
    'Digital Transformation',
    'Emergency Support',
    'Compliance & Audit'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceToggle = (service: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setSubmitSuccess(true);

    // Reset form after 3 seconds
    setTimeout(() => {
      setSubmitSuccess(false);
      setFormData({
        consultationType: 'strategic',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        industry: '',
        employeeCount: '',
        urgency: 'normal',
        services: [],
        message: '',
        preferredContact: 'email',
        preferredTime: 'morning'
      });
    }, 3000);
  };

  return (
    <section id="consultation-form" className="py-16 lg:py-20 bg-muted">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-headline font-bold text-3xl lg:text-4xl text-primary mb-4">
              Request Your Free IT Consultation
            </h2>
            <p className="font-body text-lg text-muted-foreground">
              Fill out the form below and our team will contact you within 24 hours to schedule your personalized consultation.
            </p>
          </div>

          <div className="bg-card rounded-lg shadow-elevated p-6 lg:p-8">
            {submitSuccess ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-conversion-accent rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckIcon" size={32} className="text-white" variant="solid" />
                </div>
                <h3 className="font-headline font-bold text-2xl text-primary mb-2">
                  Thank You for Reaching Out!
                </h3>
                <p className="font-body text-muted-foreground mb-4">
                  We&apos;ve received your consultation request and will contact you within 24 hours.
                </p>
                <p className="font-body text-sm text-muted-foreground">
                  Check your email for a confirmation message with next steps.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Consultation Type */}
                <div>
                  <label className="block font-body font-semibold text-sm text-foreground mb-3">
                    Consultation Type *
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { value: 'strategic', label: 'Strategic Planning', icon: 'LightBulbIcon' },
                      { value: 'emergency', label: 'Emergency Support', icon: 'ExclamationTriangleIcon' },
                      { value: 'general', label: 'General Inquiry', icon: 'ChatBubbleLeftRightIcon' }
                    ].map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, consultationType: type.value }))}
                        className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                          formData.consultationType === type.value
                            ? 'border-brand-primary bg-brand-primary/10 text-brand-primary' :'border-border bg-background text-muted-foreground hover:border-brand-primary/50'
                        }`}
                      >
                        <Icon name={type.icon as any} size={20} />
                        <span className="font-body font-semibold text-sm">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Name Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block font-body font-semibold text-sm text-foreground mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-background border border-input rounded-lg font-body text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block font-body font-semibold text-sm text-foreground mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-background border border-input rounded-lg font-body text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                {/* Contact Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block font-body font-semibold text-sm text-foreground mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-background border border-input rounded-lg font-body text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200"
                      placeholder="john.doe@company.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block font-body font-semibold text-sm text-foreground mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-background border border-input rounded-lg font-body text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                {/* Company & Industry */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="company" className="block font-body font-semibold text-sm text-foreground mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-background border border-input rounded-lg font-body text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200"
                      placeholder="Acme Corporation"
                    />
                  </div>
                  <div>
                    <label htmlFor="industry" className="block font-body font-semibold text-sm text-foreground mb-2">
                      Industry *
                    </label>
                    <select
                      id="industry"
                      name="industry"
                      value={formData.industry}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-background border border-input rounded-lg font-body text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200"
                    >
                      <option value="">Select Industry</option>
                      {industries.map((industry) => (
                        <option key={industry} value={industry}>
                          {industry}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Employee Count & Urgency */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="employeeCount" className="block font-body font-semibold text-sm text-foreground mb-2">
                      Number of Employees *
                    </label>
                    <select
                      id="employeeCount"
                      name="employeeCount"
                      value={formData.employeeCount}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-background border border-input rounded-lg font-body text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200"
                    >
                      <option value="">Select Range</option>
                      <option value="1-10">1-10</option>
                      <option value="11-50">11-50</option>
                      <option value="51-200">51-200</option>
                      <option value="201-500">201-500</option>
                      <option value="500+">500+</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="urgency" className="block font-body font-semibold text-sm text-foreground mb-2">
                      Urgency Level *
                    </label>
                    <select
                      id="urgency"
                      name="urgency"
                      value={formData.urgency}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-background border border-input rounded-lg font-body text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200"
                    >
                      <option value="normal">Normal (1-2 days)</option>
                      <option value="high">High (Same day)</option>
                      <option value="critical">Critical (Immediate)</option>
                    </select>
                  </div>
                </div>

                {/* Services of Interest */}
                <div>
                  <label className="block font-body font-semibold text-sm text-foreground mb-3">
                    Services of Interest (Select all that apply)
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {serviceOptions.map((service) => (
                      <button
                        key={service}
                        type="button"
                        onClick={() => handleServiceToggle(service)}
                        className={`flex items-center space-x-2 px-4 py-3 rounded-lg border transition-all duration-200 ${
                          formData.services.includes(service)
                            ? 'border-brand-primary bg-brand-primary/10 text-brand-primary' :'border-border bg-background text-muted-foreground hover:border-brand-primary/50'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          formData.services.includes(service)
                            ? 'border-brand-primary bg-brand-primary' :'border-muted-foreground'
                        }`}>
                          {formData.services.includes(service) && (
                            <Icon name="CheckIcon" size={14} className="text-white" variant="solid" />
                          )}
                        </div>
                        <span className="font-body text-sm">{service}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block font-body font-semibold text-sm text-foreground mb-2">
                    Tell Us About Your Needs *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 bg-background border border-input rounded-lg font-body text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200 resize-none"
                    placeholder="Please describe your current IT challenges, goals, or specific requirements..."
                  />
                </div>

                {/* Preferred Contact Method & Time */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="preferredContact" className="block font-body font-semibold text-sm text-foreground mb-2">
                      Preferred Contact Method
                    </label>
                    <select
                      id="preferredContact"
                      name="preferredContact"
                      value={formData.preferredContact}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-background border border-input rounded-lg font-body text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200"
                    >
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="either">Either</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="preferredTime" className="block font-body font-semibold text-sm text-foreground mb-2">
                      Preferred Time
                    </label>
                    <select
                      id="preferredTime"
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-background border border-input rounded-lg font-body text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200"
                    >
                      <option value="morning">Morning (9AM-12PM)</option>
                      <option value="afternoon">Afternoon (12PM-5PM)</option>
                      <option value="evening">Evening (5PM-8PM)</option>
                    </select>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center space-x-2 px-8 py-4 bg-brand-conversion hover:bg-brand-conversion/90 disabled:bg-muted-foreground disabled:cursor-not-allowed text-white rounded-lg font-cta font-semibold text-base shadow-elevated hover:shadow-card transition-all duration-300 transform hover:scale-105 disabled:transform-none"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <Icon name="PaperAirplaneIcon" size={20} variant="solid" />
                        <span>Submit Consultation Request</span>
                      </>
                    )}
                  </button>
                  <p className="text-xs font-body text-muted-foreground text-center mt-3">
                    By submitting this form, you agree to our privacy policy and terms of service.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}